import type Phaser from "phaser";

const CG_DIAG = (import.meta.env["VITE_WEB_AD_PROVIDER"] as string | undefined) === "crazygames";

function diag(message: string, data?: Record<string, unknown>): void {
  if (!CG_DIAG) return;
  console.info(`[CG-DIAG][BannerSlot] ${message}`, data ?? {});
}

export type CrazyGamesBannerSurface = "shop" | "codex" | "settings";

export interface CrazyGamesBannerSlotRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly visible: boolean;
}

export interface CrazyGamesBannerSlot extends CrazyGamesBannerSlotRect {
  readonly surface: CrazyGamesBannerSurface;
}

export const CRAZY_GAMES_INLINE_BANNER_SLOT = {
  minWidth: 320,
  minHeight: 50,
  height: 60,
  gapY: 8,
} as const;

const SURFACE_PRIORITY: readonly CrazyGamesBannerSurface[] = ["settings", "shop", "codex"];
export const CRAZY_GAMES_BANNER_SLOT_EVENT = "crazygamesbannerslotchange";
const slots = new Map<CrazyGamesBannerSurface, CrazyGamesBannerSlotRect>();

function emitSlotChange(): void {
  if (typeof window === "undefined") return;
  diag("slotChange", { slot: getCrazyGamesBannerSlot() });
  window.dispatchEvent(new CustomEvent(CRAZY_GAMES_BANNER_SLOT_EVENT, { detail: getCrazyGamesBannerSlot() }));
}

export function subscribeCrazyGamesBannerSlotChange(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CRAZY_GAMES_BANNER_SLOT_EVENT, listener);
  return () => window.removeEventListener(CRAZY_GAMES_BANNER_SLOT_EVENT, listener);
}

export function setCrazyGamesBannerSlot(
  surface: CrazyGamesBannerSurface,
  rect: CrazyGamesBannerSlotRect,
): void {
  slots.set(surface, rect);
  diag("setSlot", { surface, rect });
  emitSlotChange();
}

export function clearCrazyGamesBannerSlot(surface?: CrazyGamesBannerSurface): void {
  diag("clearSlot", { surface: surface ?? "all" });
  if (surface) {
    slots.delete(surface);
  } else {
    slots.clear();
  }
  emitSlotChange();
}

export function getCrazyGamesBannerSlot(): CrazyGamesBannerSlot | null {
  for (const surface of SURFACE_PRIORITY) {
    const rect = slots.get(surface);
    if (rect?.visible && rect.width > 0 && rect.height > 0) {
      return { surface, ...rect };
    }
  }
  return null;
}

export function phaserRectToBannerSlot(
  scene: Phaser.Scene,
  rect: { x: number; y: number; width: number; height: number },
): CrazyGamesBannerSlotRect {
  const canvas = scene.game.canvas;
  const bounds = canvas.getBoundingClientRect();
  const scaleX = bounds.width / scene.scale.width;
  const scaleY = bounds.height / scene.scale.height;
  return {
    left: bounds.left + rect.x * scaleX,
    top: bounds.top + rect.y * scaleY,
    width: rect.width * scaleX,
    height: rect.height * scaleY,
    visible: true,
  };
}

export function setCrazyGamesBannerSlotFromPhaser(
  surface: CrazyGamesBannerSurface,
  scene: Phaser.Scene,
  rect: { x: number; y: number; width: number; height: number },
): void {
  setCrazyGamesBannerSlot(surface, phaserRectToBannerSlot(scene, rect));
}
